import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import {
  IForgotPassword,
  ILoginEmpresa,
  ILoginFuncionario
} from './auth.model';
import { UserIpService } from '../UserIpService/userIp.service';
import { ILoginMotoristaExternoBody } from '../MotoristaExternoService/motoristaExterno.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _isTokenValid: boolean = false;
  private ip: string = '';
  private readonly API_URL = `${environment.baseURL}MotoristaExterno/LoginMotoristaExterno`;
  private dadoLogin = new BehaviorSubject({
    idEmpresa: '',
    nome: '',
    Token: '',
    RefreshToken: '',
    RefreshTokenExpireDate: '',
    role: ''
  });
  dadoLogin$ = this.dadoLogin.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private snackBar: MatSnackBar,
    private userIpService: UserIpService
  ) {
    this.getIpAddress();

    const rawDadoLogin = localStorage.getItem('DADO_LOGIN');
    if (!rawDadoLogin) {
      return;
    }

    this.dadoLogin.next(JSON.parse(rawDadoLogin));
  }

  get isTokenValid(): boolean {
    return this._isTokenValid;
  }

  login(email: string, password: string) {
    const body: ILoginMotoristaExternoBody = {
      TX_MAIL_EXTR: email,
      TX_SENH_EXTR: password
    };

    return this.http
      .post<{
        IdMotorista?: number;
        NmMotorista?: string;
        Token?: string;
        RefreshToken?: string;
        RefreshTokenExpireDate?: string;
      }>(this.API_URL, body)
      .pipe(
        tap(response => {
          this.saveToken(response.Token ?? '');
          const { valid, tokenDecoded } = this.isTokenValidToken(
            response.Token ?? ''
          );
          this._isTokenValid = valid;
          localStorage.setItem(
            'DADO_LOGIN',
            JSON.stringify({ ...tokenDecoded, ...response })
          );

          localStorage.setItem('id', String(response.IdMotorista) ?? '');
          localStorage.setItem('nome', response.NmMotorista ?? '');
          localStorage.setItem('role', 'externo');
          localStorage.setItem('ipv4', this.ip);
          this.dadoLogin.next({ ...tokenDecoded, ...response } as any);
        }),
        catchError(error => {
          return throwError(error);
        })
      );
  }

  logout(): void {
    this.removeToken();
    this._isTokenValid = false;
    this.router.navigate(['login']);
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    this._isTokenValid = token !== null && this.isTokenValidToken(token).valid;
    return this._isTokenValid;
  }

  private saveToken(token: string): void {
    localStorage.setItem('token', token);
  }

  private removeToken(): void {
    localStorage.removeItem('token');
  }

  private isTokenValidToken(token: string): {
    valid: boolean;
    tokenDecoded?: any;
  } {
    try {
      const tokenDecoded = jwtDecode<{ exp: number }>(token);
      const currentTime = new Date().getTime() / 1000;
      return { valid: tokenDecoded.exp > currentTime, tokenDecoded };
    } catch {
      return { valid: false };
    }
  }

  isAuthenticatedAndTokenValid() {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    const tokenData = this.getTokenData(token);
    const expiration = new Date(tokenData.exp * 1000);

    return expiration > new Date();
  }

  private getTokenData(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const decodedData = JSON.parse(atob(base64));
    return decodedData;
  }

  private openSnackBar(message: string, action: string): void {
    this.snackBar.open(message, action, { panelClass: ['error-snackbar'] });
  }

  public forgotPassword(body: IForgotPassword): Observable<any> {
    return this.http.post<any>(
      `${environment.baseURL}Email/SendForgotPasswordEmail`,
      body
    );
  }

  getIpAddress(): void {
    const res = this.userIpService.getIpAddress().subscribe({
      next: (res: any) => {
        this.ip = res.ip;
      }
    });
  }
}
